import React, {InputHTMLAttributes, Ref, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {style, classes} from './checkbox.st.css';
import {reactCreateMemo, reactForwardRef, RenderFunction, useBoxedValue, useInstanceValue} from '../index';
import {Evaluable, IdGenerator, ObservableValue, OmitStrict, Values} from '@wix/devzai-utils-common';
import {evaluateWhenFunction} from '@wix/devzai-utils-common';
import {assertNotNullable} from '@wix/devzai-utils-common';

export const CheckboxCheckedState = {
    Checked: true,
    Unchecked: false,
    Mixed: 'mixed'
} as const;

export type CheckboxCheckedState = boolean | 'mixed';

export const CheckboxRenderingMode = {
    Interactive: 'interactive',
    StatusOnly: 'statusOnly'
} as const;

export type CheckboxRenderingMode = Values<typeof CheckboxRenderingMode>;

export namespace CheckboxCheckedState {
    export type SupportMixedState = boolean | 'mixed';
    export type Boolean = boolean;
}

export namespace CheckboxComponent {

    export interface ChildrenRenderProps<CHECKED_STATE extends CheckboxCheckedState = boolean> {
        checked: CHECKED_STATE;
        checkbox: Checkbox<CHECKED_STATE>;
    }

    export type ChildrenRenderFunction<CHECKED_STATE extends CheckboxCheckedState = boolean> = RenderFunction<ChildrenRenderProps<CHECKED_STATE>, React.ReactNode>;

    export interface Props<CHECKED_STATE extends CheckboxCheckedState = boolean> extends OmitStrict<React.HTMLAttributes<any>, 'defaultChecked'> {
        children?: Evaluable<ChildrenRenderFunction<CHECKED_STATE>>;
        onCheckedChanged?: (isChecked: CHECKED_STATE) => void;
        checked?: CHECKED_STATE;
        defaultChecked?: CHECKED_STATE;
        ref?: Ref<Checkbox<CHECKED_STATE>>
        nativeCheckboxAttrs?: InputHTMLAttributes<any>;
        focusable?: boolean;
        inputId?: string;
        disabled?: boolean;
        renderingMode?: CheckboxRenderingMode;
    }
}

export interface Checkbox<CHECKED_STATE extends CheckboxCheckedState = boolean> {
    isChecked () : CHECKED_STATE;
    toggleChecked (checked?: CHECKED_STATE): void;
    getObservableChecked () : ObservableValue<CHECKED_STATE>;
}

export const Checkbox = reactCreateMemo(reactForwardRef(function Checkbox<CHECKED_STATE extends CheckboxCheckedState = boolean> (props: CheckboxComponent.Props<CHECKED_STATE>) {
    const {
        ref,
        children,
        onCheckedChanged,
        checked,
        defaultChecked,
        nativeCheckboxAttrs,
        focusable,
        inputId,
        disabled = false,
        renderingMode = CheckboxRenderingMode.Interactive,

        className,
        ...htmlAttributes
    } = props;

    const propsRef = useRef<CheckboxComponent.Props<CHECKED_STATE>>();
    propsRef.current = props;

    const generatedCheckboxId = useInstanceValue(() => IdGenerator.uniqueId());
    const checkboxId = inputId ?? generatedCheckboxId;

    const isControlled = useInstanceValue(() => {
        const isControlled = checked !== undefined;

        if (isControlled && defaultChecked !== undefined) {
            console.warn(`When Checkbox is controlled, defaultIsChecked property is redundant`);
        }

        return isControlled;
    });

    if (isControlled && checked === undefined) {
        throw new Error(`A component is changing an uncontrolled Checkbox to be controlled.`);
    }

    const [uncontrolledIsChecked, setUncontrolledIsChecked] = useState<CHECKED_STATE>(defaultChecked as CHECKED_STATE ?? false);

    const resolvedIsChecked = assertNotNullable(isControlled ? checked : uncontrolledIsChecked);

    const renderStateBoxed = useBoxedValue({
        isChecked: resolvedIsChecked,
        isControlled: isControlled,
        setUncontrolledIsChecked,
        onCheckedChanged
    });

    const observableChecked = useInstanceValue(() => new ObservableValue<CHECKED_STATE>(resolvedIsChecked));

    useLayoutEffect(() => {
        if (isControlled) {
            observableChecked.setValue(resolvedIsChecked);
        }
    }, [resolvedIsChecked, observableChecked, isControlled]);

    const checkbox = useMemo<Checkbox<CHECKED_STATE>>(() => {
        return {
            isChecked () {
                return assertNotNullable(renderStateBoxed.current).isChecked;
            },
            toggleChecked (checked?: CHECKED_STATE) {
                const renderState = assertNotNullable(renderStateBoxed.current);

                const updatedChecked = (checked ?? renderState.isChecked === false) as CHECKED_STATE;

                if (!renderState.isControlled) {
                    renderState.setUncontrolledIsChecked(updatedChecked);
                }

                const onCheckedChanged = renderState.onCheckedChanged;
                if (onCheckedChanged) {
                    onCheckedChanged(updatedChecked);
                }
            },

            getObservableChecked () {
                return observableChecked;
            }
        }
    }, [renderStateBoxed, observableChecked]);

    useImperativeHandle(ref, () => checkbox, [checkbox]);

    const evaluatedChildren = evaluateWhenFunction(children, {
        checked: resolvedIsChecked,
        checkbox: checkbox
    });

    return (
        <div
            {...htmlAttributes}
            className={style(classes.root, {
                checked: resolvedIsChecked !== false,
                disabled: disabled,
                renderingMode: renderingMode
            }, className)}
        >
            {renderingMode === CheckboxRenderingMode.StatusOnly ? evaluatedChildren : (
                <>
                    <input
                        tabIndex={focusable ? undefined : -1}
                        {...nativeCheckboxAttrs}
                        disabled={disabled}
                        className={style(classes.nativeCheckbox, {rendered: !children})}
                        id={checkboxId}
                        type={'checkbox'}
                        checked={resolvedIsChecked === true || resolvedIsChecked === CheckboxCheckedState.Mixed}
                        onChange={event => {
                            const updatedIsChecked = event.target.checked as CHECKED_STATE;

                            if (!isControlled) {
                                setUncontrolledIsChecked(updatedIsChecked);
                                observableChecked.setValue(updatedIsChecked);
                            }

                            if (onCheckedChanged) {
                                onCheckedChanged(updatedIsChecked);
                            }
                        }}
                    />
                    {evaluatedChildren ? (
                        <label
                            className={style(classes.label)}
                            htmlFor={checkboxId}
                        >
                            {evaluatedChildren}
                        </label>
                    ) : null}
                </>
            )}
        </div>
    );
}));
